import React, { useState, useEffect, useCallback, useRef } from 'react';
import { RollData, DetailedCoin } from '../types';
import { PlayerProfile, processRollCoin } from '../lib/gameEngine';
import { SetSeriesItem } from '../lib/albumData';
import { Header } from './Header';
import { CoinBadges } from './CoinBadges';
import { ImageBox } from './ImageBox';
import { DetailsBox } from './DetailsBox';
import { RollComplete } from './RollComplete';
import { Controls } from './Controls';
import { InlineSaveBanner } from './InlineSaveBanner';
import { SetSeriesProgressBar, computeContributingSets } from './SetSeriesProgressBar';
import { getCoinAnimationTimings, getRarityVisualEffect } from '../config/animationConfig';
import confetti from 'canvas-confetti';

export interface HuntingViewProps {
  currentRoll: RollData | null;
  currentIndex: number;
  setCurrentIndex: React.Dispatch<React.SetStateAction<number>>;
  isRollFinished: boolean;
  setIsRollFinished: React.Dispatch<React.SetStateAction<boolean>>;
  setCurrentRoll: React.Dispatch<React.SetStateAction<RollData | null>>;
  profile: PlayerProfile;
  setProfile: React.Dispatch<React.SetStateAction<PlayerProfile>>;
  setsData: SetSeriesItem[];
  userAlbum: Record<string, number>;
  setUserAlbum: React.Dispatch<React.SetStateAction<Record<string, number>>>;
  loading: boolean;
  onBackToBank: () => void;
  onOpenSaveModal: () => void;
  triggerCoinName: string | undefined;
  setTriggerCoinName: (name: string | undefined) => void;
  isBannerVisible: boolean;
  setIsBannerVisible: (visible: boolean) => void;
  refreshProfileAndRolls: () => Promise<void>;
  onTriggerScreenFlash: (flashColor: string) => void;
}

export const HuntingView: React.FC<HuntingViewProps> = ({
  currentRoll,
  currentIndex,
  setCurrentIndex,
  isRollFinished,
  setIsRollFinished,
  setCurrentRoll,
  profile,
  setProfile,
  setsData,
  userAlbum,
  setUserAlbum,
  loading,
  onBackToBank,
  onOpenSaveModal,
  triggerCoinName,
  setTriggerCoinName,
  isBannerVisible,
  setIsBannerVisible,
  refreshProfileAndRolls,
  onTriggerScreenFlash
}) => {
  const [isCoinRevealed, setIsCoinRevealed] = useState<boolean>(false);
  const [isRarityRevealed, setIsRarityRevealed] = useState<boolean>(false);
  const [isAnimating, setIsAnimating] = useState<boolean>(true);
  const [isExiting, setIsExiting] = useState<boolean>(false);
  const hasPromptedRareRef = useRef<boolean>(false);
  const pendingCoinActionRef = useRef<'keep' | 'quick_sell' | 'quick_deposit'>('keep');

  const stateRef = useRef({
    currentRoll,
    currentIndex,
    isRollFinished,
    loading,
    profile,
    isAnimating,
    isExiting,
    setsData,
    userAlbum
  });
  stateRef.current = {
    currentRoll,
    currentIndex,
    isRollFinished,
    loading,
    profile,
    isAnimating,
    isExiting,
    setsData,
    userAlbum
  };

  // Reset animation states whenever a new coin is queued in hunting view
  useEffect(() => {
    if (!isRollFinished && currentRoll) {
      const curCoin = currentRoll.coins[currentIndex]?.coin;
      const timings = getCoinAnimationTimings(curCoin);
      // If animation doesn't flip, coin details and mintage counter start immediately
      setIsCoinRevealed(!timings.shouldFlip);
      setIsRarityRevealed(false);
      setIsAnimating(true);
    } else {
      setIsCoinRevealed(true);
      setIsRarityRevealed(true);
      setIsAnimating(false);
    }
  }, [currentIndex, currentRoll?.id, isRollFinished, currentRoll]);

  const handleFlipComplete = useCallback(() => {
    setIsCoinRevealed(true);
  }, []);

  const handleCountComplete = useCallback(() => {
    setIsRarityRevealed(true);
    const { currentRoll: roll, currentIndex: idx, profile: prof } = stateRef.current;
    const revealedCoin = roll?.coins[idx]?.coin;
    const timings = getCoinAnimationTimings(revealedCoin);
    const effects = getRarityVisualEffect(timings.rarityKey);

    // Trigger Screen Flash & Particle Fireworks when mintage reveal stops at a rare tier
    if (effects.hasEffects) {
      onTriggerScreenFlash(effects.flashColor);

      try {
        confetti({
          particleCount: effects.particleCount,
          spread: 80,
          origin: { y: 0.42 },
          colors: effects.confettiColors,
          disableForReducedMotion: true,
          ticks: 200,
          gravity: 1.1,
          scalar: 0.9,
        });
      } catch {
        // Fallback silently if canvas is unavailable
      }
    }

    // Check if newly revealed coin completes any sets, and fire green confetti celebration
    const rollCoins = roll?.coins || [];
    const rollNewFindIds = new Set<string>();
    for (let i = 0; i < idx; i++) {
      const cState = rollCoins[i];
      if (cState?.isNewFind && cState.coin?.id) {
        rollNewFindIds.add(cState.coin.id);
      }
    }
    const currentCoinState = rollCoins[idx];
    const contributing = computeContributingSets(
      revealedCoin,
      currentCoinState?.isNewFind,
      stateRef.current.setsData,
      stateRef.current.userAlbum,
      rollNewFindIds
    );

    const hasNewlyCompletedSet = contributing.some(s => s.isNewlyCompleted);
    if (hasNewlyCompletedSet) {
      try {
        confetti({
          particleCount: 110,
          spread: 90,
          origin: { y: 0.65 },
          colors: ['#10b981', '#059669', '#34d399', '#6ee7b7', '#15803d', '#a7f3d0'],
          disableForReducedMotion: true,
          ticks: 250,
          gravity: 1.0,
          scalar: 1.0,
        });
      } catch {}
    }

    setTimeout(() => {
      setIsAnimating(false);

      // Contextual Inline Card Trigger: Check if newly revealed coin is rare (<=1M or NIFC)
      if (
        revealedCoin &&
        !hasPromptedRareRef.current &&
        prof.isGuest &&
        (revealedCoin.mintage === 0 || revealedCoin.mintage <= 1_000_000)
      ) {
        hasPromptedRareRef.current = true;
        setTriggerCoinName(revealedCoin.name || `${revealedCoin.country} 2€ (${revealedCoin.year})`);
        setIsBannerVisible(true);
      }
    }, timings.inputBufferMs);
  }, [onTriggerScreenFlash, setTriggerCoinName, setIsBannerVisible]);

  const handleExitComplete = useCallback(() => {
    const { currentRoll: roll, currentIndex: idx } = stateRef.current;
    if (!roll) {
      setIsExiting(false);
      setIsAnimating(false);
      return;
    }

    const action = pendingCoinActionRef.current || 'keep';
    const coinId = roll.coins[idx]?.coin?.id;
    setIsExiting(false);

    // Atomically commit coin action on server
    processRollCoin(roll.rollType || 'standard_2euro', idx, action)
      .then((res) => {
        if (res && typeof res.wallet === 'number') {
          setProfile(prev => ({ ...prev, wallet: res.wallet! }));
        }
        if (action === 'keep' && coinId) {
          setUserAlbum(prev => ({
            ...prev,
            [coinId]: (prev[coinId] || 0) + 1
          }));
        }
      })
      .catch((err) => {
        console.error('Error processing roll coin:', err);
      });

    const totalCoins = roll.coins.length || 25;
    if (idx < totalCoins - 1) {
      const nextIdx = idx + 1;
      setCurrentIndex(nextIdx);
    } else {
      setIsRollFinished(true);
      setIsAnimating(false);
      refreshProfileAndRolls();
    }
  }, [refreshProfileAndRolls, setCurrentIndex, setIsRollFinished, setProfile, setUserAlbum]);

  const handleNextAction = useCallback((action: 'keep' | 'quick_sell' | 'quick_deposit' = 'keep') => {
    const {
      currentRoll: roll,
      isRollFinished: finished,
      loading: isLoading,
      isAnimating: animating,
      isExiting: exiting
    } = stateRef.current;

    // Block next action while loading, during exiting, or during unskippable animations
    if (isLoading || exiting || (!finished && animating)) return;

    // When roll is complete, clicking the button returns player to the Bank
    if (finished || !roll) {
      onBackToBank();
      return;
    }

    // Set pending action and start coin exit to the right with stats fade-out
    pendingCoinActionRef.current = action;
    setIsExiting(true);
    setIsAnimating(true);
  }, [onBackToBank]);

  // Keyboard navigation when in hunting mode
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        !stateRef.current.isRollFinished &&
        !stateRef.current.isAnimating &&
        !stateRef.current.isExiting &&
        !stateRef.current.loading
      ) {
        if (e.code === 'Space' || e.code === 'Enter' || e.code === 'ArrowRight') {
          e.preventDefault();
          handleNextAction('keep');
        } else if (e.code === 'KeyS') {
          e.preventDefault();
          const { currentRoll: roll, currentIndex: idx } = stateRef.current;
          const currentVal = roll?.coins?.[idx]?.valuation?.marketValue || 2.00;
          const currentFace = roll?.coins?.[idx]?.valuation?.faceValue || 2.00;
          handleNextAction(currentVal > currentFace ? 'quick_sell' : 'quick_deposit');
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleNextAction]);

  const currentCoinState = currentRoll?.coins?.[currentIndex];
  const coin = currentCoinState?.coin;
  const rollTotalCoins = currentRoll?.coins?.length || 25;
  const coinFaceValue = currentCoinState?.valuation?.faceValue ?? 2.00;

  if (!currentRoll) {
    return (
      <div className="hunting-view" style={{ textAlign: 'center', padding: '60px 20px', color: '#64748b' }}>
        <div style={{ fontSize: '18px', fontWeight: 600, marginBottom: '16px' }}>Loading coin roll...</div>
        <button className="back-btn" onClick={onBackToBank} style={{ margin: '0 auto', display: 'inline-flex' }}>
          ← Back to Bank
        </button>
      </div>
    );
  }

  return (
    <div className="hunting-view">
      <div className="hunting-nav">
        <button
          className="back-btn"
          onClick={onBackToBank}
        >
          ← Back to Bank
        </button>
      </div>

      <Header
        currentIndex={currentIndex}
        isRollFinished={isRollFinished}
        totalCoins={rollTotalCoins}
        rollType={currentRoll?.rollType}
      />

      {!isRollFinished && coin && (
        <CoinBadges
          coin={coin}
          isNewFind={currentCoinState?.isNewFind}
          timesFoundSoFar={currentCoinState?.timesFoundSoFar}
          revealed={isCoinRevealed}
          showRarityBadge={isRarityRevealed}
          isExiting={isExiting}
        />
      )}

      {isRollFinished && currentRoll ? (
        <RollComplete roll={currentRoll} />
      ) : (
        <div className="content-box">
          <ImageBox
            imagePath={coin?.imagePath}
            denomination={coin?.denomination}
            year={coin?.year}
            coinId={coin ? `${coin.id}_${currentIndex}` : undefined}
            isAnimating={isAnimating && !isExiting && (getCoinAnimationTimings(coin).shouldFlip ? !isCoinRevealed : true)}
            isExiting={isExiting}
            shouldFlip={getCoinAnimationTimings(coin).shouldFlip}
            enterDurationMs={getCoinAnimationTimings(coin).enterAndFlipMs}
            exitDurationMs={getCoinAnimationTimings(coin).exitMs}
            auraGradient={isCoinRevealed && !isExiting ? getRarityVisualEffect(getCoinAnimationTimings(coin).rarityKey).auraGradient : undefined}
            onFlipComplete={handleFlipComplete}
            onExitComplete={handleExitComplete}
          />
          <DetailsBox
            coin={coin}
            revealed={isCoinRevealed}
            isCountComplete={isRarityRevealed}
            isExiting={isExiting}
            countUpDurationMs={getCoinAnimationTimings(coin).countUpMs}
            onCountComplete={handleCountComplete}
          />
        </div>
      )}

      {/* Non-Intrusive Inline Card Triggered by Rare Finds */}
      {isBannerVisible && profile.isGuest && (
        <InlineSaveBanner
          triggerCoinName={triggerCoinName}
          onOpenModal={onOpenSaveModal}
          onClose={() => setIsBannerVisible(false)}
        />
      )}

      <Controls
        currentIndex={currentIndex}
        totalCoins={rollTotalCoins}
        faceValue={coinFaceValue}
        isRollFinished={isRollFinished}
        hasRoll={Boolean(currentRoll)}
        disabled={loading || (!isRollFinished && (isAnimating || isExiting))}
        marketValue={currentCoinState?.valuation?.marketValue}
        onAction={() => handleNextAction('keep')}
        onQuickAction={() => {
          const marketVal = currentCoinState?.valuation?.marketValue || coinFaceValue;
          const isMarkup = marketVal > coinFaceValue;
          handleNextAction(isMarkup ? 'quick_sell' : 'quick_deposit');
        }}
      />

      {/* Set / Series Progress Bar positioned strictly below control buttons, appearing after mintage completes counting */}
      {!isRollFinished && coin && currentCoinState?.isNewFind && (
        <SetSeriesProgressBar
          coin={coin}
          isNewFind={currentCoinState?.isNewFind}
          sets={setsData}
          userAlbum={userAlbum}
          rollNewFindIds={new Set(
            (currentRoll?.coins || [])
              .slice(0, currentIndex)
              .filter(c => c.isNewFind && c.coin?.id)
              .map(c => c.coin.id)
          )}
          revealed={isRarityRevealed}
          isExiting={isExiting}
        />
      )}
    </div>
  );
};
